import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { ArrowRight, ShieldCheck, KeyRound, X } from "lucide-react";

const ADMIN_EMAIL_HINT = process.env.REACT_APP_ADMIN_EMAIL_HINT || "";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const adminMode = params.get("as") === "admin";
  const [email, setEmail] = useState(adminMode ? ADMIN_EMAIL_HINT : "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    if (adminMode && ADMIN_EMAIL_HINT) setEmail(ADMIN_EMAIL_HINT);
  }, [adminMode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      const from = loc.state?.from || "/dashboard";
      nav(from);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-brand-yellow border-2 border-brand-ink shadow-brutal flex items-center justify-center">
            <span className="font-display font-bold text-2xl text-brand-navy">L</span>
          </div>
          <span className="font-display font-bold text-3xl text-brand-navy">LingoFacil</span>
        </Link>
        <div className="brutal-card p-8">
          {adminMode && (
            <div className="brutal-badge bg-brand-pink text-white mb-3" data-testid="login-admin-badge">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={3} /> Admin sign-in
            </div>
          )}
          <h1 className="font-display text-4xl font-bold text-brand-navy mb-2">
            {adminMode ? "Admin, welcome back" : "Welcome back!"}
          </h1>
          <p className="text-brand-navy/70 mb-6">
            {adminMode
              ? "Sign in to unlock the Studio."
              : "Log in to keep your streak alive."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="brutal-input mt-1"
                placeholder="you@lingofacil.com"
                autoFocus={!adminMode}
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="brutal-input mt-1"
                placeholder="••••••••"
                autoFocus={adminMode}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="brutal-btn-pink w-full text-lg py-4"
            >
              {busy ? "Logging in..." : (<>Log in <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)}
            </button>
          </form>

          <div className="mt-6 space-y-3 text-center">
            {adminMode && (
              <button
                type="button"
                onClick={() => setResetOpen(true)}
                data-testid="login-forgot-password"
                className="inline-flex items-center gap-1 text-xs font-bold text-brand-navy/60 hover:text-brand-navy underline"
              >
                <KeyRound className="w-3 h-3" strokeWidth={3} /> Forgot my password
              </button>
            )}
            {adminMode ? (
              <Link
                to="/login"
                data-testid="login-switch-user"
                className="text-xs font-bold text-brand-navy/60 hover:text-brand-navy underline"
              >
                Sign in as a different user
              </Link>
            ) : (
              <Link
                to="/login?as=admin"
                data-testid="login-switch-admin"
                className="inline-flex items-center gap-1 text-xs font-bold text-brand-navy/60 hover:text-brand-navy underline"
              >
                <ShieldCheck className="w-3 h-3" strokeWidth={3} /> Log in as admin
              </Link>
            )}
          </div>
        </div>
      </div>

      <ForgotPasswordModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        defaultEmail={email || ADMIN_EMAIL_HINT}
        onReset={(newPassword) => {
          setPassword(newPassword);
          setResetOpen(false);
        }}
      />
    </div>
  );
}

function ForgotPasswordModal({ open, onClose, defaultEmail, onReset }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(defaultEmail || "");
      setToken("");
      setPw("");
      setPw2("");
    }
  }, [open, defaultEmail]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw !== pw2) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/emergency-recover", {
        email,
        new_password: pw,
        recovery_token: token,
      });
      toast.success("Password reset — signing you in.");
      onReset(pw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Check the recovery token.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="brutal-card p-6 bg-white max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="forgot-password-modal"
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <div>
            <div className="brutal-badge bg-brand-yellow text-xs mb-2">
              <KeyRound className="w-3.5 h-3.5" strokeWidth={3} /> Recovery
            </div>
            <h3 className="font-display text-2xl font-bold text-brand-navy">Reset admin password</h3>
            <p className="text-sm text-brand-navy/60 mt-1">
              Enter the <span className="font-mono">RECOVERY_TOKEN</span> from your backend environment.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="forgot-password-close"
            className="p-2 hover:bg-brand-cream rounded-full shrink-0"
            title="Close"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-brand-navy tracking-wide">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              className="brutal-input mt-1"
              placeholder="admin@example.com"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-brand-navy tracking-wide">Recovery token</label>
            <input
              type="password"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="forgot-token"
              className="brutal-input mt-1 font-mono text-sm"
              placeholder="lingofacil-recovery-…"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-brand-navy tracking-wide">New password</label>
            <input
              type="password"
              required
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="forgot-new-password"
              className="brutal-input mt-1"
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-brand-navy tracking-wide">Confirm new password</label>
            <input
              type="password"
              required
              minLength={8}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              data-testid="forgot-confirm-password"
              className="brutal-input mt-1"
              placeholder="Repeat it"
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="forgot-submit"
            className="brutal-btn-pink w-full py-3"
          >
            {busy ? "Resetting…" : "Reset password"}
          </button>
          <p className="text-xs text-brand-navy/60 leading-relaxed">
            Don't have the token? Ask a co-admin or read it from{" "}
            <span className="font-mono">backend/.env → RECOVERY_TOKEN</span>. Rotate it right after use.
          </p>
        </form>
      </div>
    </div>
  );
}
